import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Users,
  Plus,
  Trash2,
  Shield,
  Eye,
  Edit3,
  ArrowLeft,
  Zap,
  Loader2,
  Mail,
  MoreVertical,
  Building2,
  Database,
  Download,
} from "lucide-react";
import { exportUsersCsv } from "@/lib/csvExport";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useNavigate } from "react-router-dom";
import { useUsers, PlatformUser } from "@/hooks/useUsers";
import { useAuth } from "@/hooks/useAuth";
import { useRole } from "@/hooks/useRole";
import { supabase } from "@/integrations/supabase/client";

const roleConfig = {
  DiscoRun: {
    icon: Shield,
    color: "bg-destructive/10 text-destructive border-destructive/20",
    desc: "Full access to all settings and data",
  },
  Manager: {
    icon: Edit3,
    color: "bg-primary/10 text-primary border-primary/20",
    desc: "Can manage invoices and view reports",
  },
  Viewer: {
    icon: Eye,
    color: "bg-muted text-muted-foreground border-border",
    desc: "Read-only access to dashboard",
  },
  PAR: {
    icon: Database,
    color: "bg-cyan-500/10 text-cyan-400 border-cyan-500/20",
    desc: "Access to PAR Systems page only",
  },
};

interface Company {
  id: string;
  name: string;
}

export default function UserManagementPage() {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const {
    users,
    invitations,
    isLoading,
    fetchUsers,
    inviteUser,
    updateUserRole,
    updateUserCompany,
    removeUser,
  } = useUsers();
  const { isAdmin, isManager, isLoading: isRoleLoading } = useRole();
  const [showInvite, setShowInvite] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [newName, setNewName] = useState("");
  const [newRole, setNewRole] = useState<"DiscoRun" | "Manager" | "Viewer" | "PAR">("Viewer");
  const [newCompanyId, setNewCompanyId] = useState<string | null>(null);
  const [userToRemove, setUserToRemove] = useState<PlatformUser | null>(null);
  const [isInviting, setIsInviting] = useState(false);
  const [isUpdatingRole, setIsUpdatingRole] = useState<string | null>(null);
  const [isUpdatingCompany, setIsUpdatingCompany] = useState<string | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);

  const canInvite = isAdmin;
  const canView = isManager; // Managers can view, Admins can manage

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Load backtest sessions (companies) for admin assignment UI
  useEffect(() => {
    if (!isAdmin) return;
    supabase
      .from("backtest_sessions")
      .select("id, name")
      .order("name")
      .then(({ data }) => {
        if (data) setCompanies(data);
      });
  }, [isAdmin]);

  const handleInvite = async () => {
    if (!newEmail || !newName) return;
    setIsInviting(true);
    const result = await inviteUser(newEmail, newName, newRole, newCompanyId);
    if (result.success) {
      setNewEmail("");
      setNewName("");
      setNewRole("Viewer");
      setNewCompanyId(null);
      setShowInvite(false);
    }
    setIsInviting(false);
  };

  const handleRoleChange = async (userId: string, role: "Admin" | "Manager" | "Viewer") => {
    setIsUpdatingRole(userId);
    await updateUserRole(userId, role);
    setIsUpdatingRole(null);
  };

  const handleCompanyChange = async (userId: string, companyId: string | null) => {
    setIsUpdatingCompany(userId);
    await updateUserCompany(userId, companyId);
    setIsUpdatingCompany(null);
  };

  const handleRemoveClick = (user: PlatformUser) => {
    setUserToRemove(user);
  };

  const handleRemoveConfirm = async () => {
    if (userToRemove) {
      await removeUser(userToRemove.id);
      setUserToRemove(null);
    }
  };

  // Combine users and invitations
  const allUsers = [
    ...users,
    ...invitations.map((inv) => ({
      ...inv,
      id: inv.id,
    })),
  ].sort((a, b) => {
    // Sort by status (Active first), then by name
    if (a.status !== b.status) {
      return a.status === "Active" ? -1 : 1;
    }
    return a.name.localeCompare(b.name);
  });

  if (isLoading || isRoleLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center" role="status">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Check permissions
  if (!canView) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 text-center">
          <Shield className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
          <h2 className="text-xl font-display font-bold text-foreground mb-2">Access Denied</h2>
          <p className="text-sm text-muted-foreground mb-6">
            You need Manager or Admin access to view user management.
          </p>
          <Button onClick={() => navigate("/")} variant="outline">
            Go to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="w-full px-6 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/")}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl gradient-primary flex items-center justify-center shadow-primary">
                <Zap className="w-5 h-5 text-primary-foreground" />
              </div>
              <div>
                <h1 className="text-xl font-display font-bold text-foreground">User Management</h1>
                <p className="text-sm text-muted-foreground">
                  Manage platform users and access levels • Only Admins can invite new users
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-3xl space-y-6">
        {/* Role Legend */}
        <div className="grid grid-cols-3 gap-4">
          {(Object.entries(roleConfig) as [string, typeof roleConfig.Admin][]).map(
            ([role, config]) => {
              const Icon = config.icon;
              return (
                <Card key={role} className="glass-card p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Icon className="w-4 h-4" />
                    <span className="font-semibold text-sm">{role}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{config.desc}</p>
                </Card>
              );
            }
          )}
        </div>

        {/* Users List */}
        <Card className="glass-card">
          <div className="p-6 border-b border-border flex items-center justify-between">
            <div>
              <h2 className="text-lg font-display font-semibold text-foreground flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                Team Members
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                {allUsers.length} {allUsers.length === 1 ? "member" : "members"}
                {invitations.length > 0 &&
                  ` • ${invitations.length} pending invitation${invitations.length === 1 ? "" : "s"}`}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {allUsers.length > 0 && (
                <Button variant="outline" size="sm" onClick={() => exportUsersCsv(allUsers)}>
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
              )}
              {!showInvite && canInvite && (
                <Button onClick={() => setShowInvite(true)} size="sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Invite User
                </Button>
              )}
              {!canInvite && (
                <Badge variant="outline" className="text-xs">
                  Admin only
                </Badge>
              )}
            </div>
          </div>

          {showInvite && (
            <div className="p-6 border-b border-border bg-muted/30">
              <h3 className="text-sm font-semibold mb-3">Invite New User</h3>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div className="space-y-1">
                  <Label className="text-xs">Name</Label>
                  <Input
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    placeholder="Full name"
                    className="h-9"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Email</Label>
                  <Input
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder="email@company.com"
                    className="h-9"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2 mb-3">
                <Select
                  value={newRole}
                  onValueChange={(v) => setNewRole(v as "DiscoRun" | "Manager" | "Viewer" | "PAR")}
                >
                  <SelectTrigger className="w-28 h-9 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="DiscoRun">DiscoRun</SelectItem>
                    <SelectItem value="Manager">Manager</SelectItem>
                    <SelectItem value="Viewer">Viewer</SelectItem>
                    <SelectItem value="PAR">PAR</SelectItem>
                  </SelectContent>
                </Select>
                <Select
                  value={newCompanyId ?? "none"}
                  onValueChange={(v) => setNewCompanyId(v === "none" ? null : v)}
                >
                  <SelectTrigger className="flex-1 h-9 text-xs">
                    <Building2 className="w-3 h-3 mr-1.5 text-muted-foreground" />
                    <SelectValue placeholder="No company (full access)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No company (full access)</SelectItem>
                    {companies.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1" />
                <Button variant="outline" size="sm" onClick={() => setShowInvite(false)}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleInvite}
                  disabled={!newEmail || !newName || isInviting}
                >
                  {isInviting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4 mr-2" />
                      Send Invite
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {allUsers.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
              <p className="text-sm text-muted-foreground">No team members yet</p>
              {canInvite && (
                <Button
                  onClick={() => setShowInvite(true)}
                  size="sm"
                  className="mt-4"
                  variant="outline"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Invite your first team member
                </Button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-border">
              {allUsers.map((user) => {
                const isCurrentUser = user.id === currentUser?.id;
                const canEdit = isAdmin && !isCurrentUser;
                const canDelete = isAdmin && !isCurrentUser && user.status === "Active";
                const userCompany = companies.find((c) => c.id === user.company_id);

                return (
                  <div
                    key={user.id}
                    className="flex items-center gap-3 p-4 hover:bg-muted/30 transition-colors group"
                  >
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary flex-shrink-0">
                      {user.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-medium text-foreground truncate">
                          {user.name}
                          {isCurrentUser && (
                            <span className="text-xs text-muted-foreground ml-2">(You)</span>
                          )}
                        </p>
                        {user.status === "Invited" && (
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                            <Mail className="w-3 h-3 mr-1" />
                            Invited
                          </Badge>
                        )}
                        {userCompany && (
                          <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                            <Building2 className="w-3 h-3 mr-1" />
                            {userCompany.name}
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                      {user.last_sign_in_at && user.status === "Active" && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Last active: {new Date(user.last_sign_in_at).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    {/* Company selector for admins */}
                    {canEdit && (
                      <Select
                        value={user.company_id ?? "none"}
                        onValueChange={(v) => handleCompanyChange(user.id, v === "none" ? null : v)}
                        disabled={isUpdatingCompany === user.id}
                      >
                        <SelectTrigger className="w-36 h-8 text-xs">
                          {isUpdatingCompany === user.id ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <>
                              <Building2 className="w-3 h-3 mr-1 text-muted-foreground flex-shrink-0" />
                              <SelectValue placeholder="No company" />
                            </>
                          )}
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No company</SelectItem>
                          {companies.map((c) => (
                            <SelectItem key={c.id} value={c.id}>
                              {c.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    <Select
                      value={user.role}
                      onValueChange={(v) =>
                        handleRoleChange(user.id, v as "Admin" | "Manager" | "Viewer")
                      }
                      disabled={!canEdit || isUpdatingRole === user.id}
                    >
                      <SelectTrigger className="w-32 h-8 text-xs">
                        {isUpdatingRole === user.id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <SelectValue />
                        )}
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="DiscoRun">
                          <div className="flex items-center gap-2">
                            <Shield className="w-3 h-3" />
                            DiscoRun
                          </div>
                        </SelectItem>
                        <SelectItem value="Manager">
                          <div className="flex items-center gap-2">
                            <Edit3 className="w-3 h-3" />
                            Manager
                          </div>
                        </SelectItem>
                        <SelectItem value="Viewer">
                          <div className="flex items-center gap-2">
                            <Eye className="w-3 h-3" />
                            Viewer
                          </div>
                        </SelectItem>
                        <SelectItem value="PAR">
                          <div className="flex items-center gap-2">
                            <Database className="w-3 h-3" />
                            PAR
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    {canDelete && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-muted-foreground hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => handleRemoveClick(user)}
                            className="text-destructive focus:text-destructive"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Remove user
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </main>

      {/* Remove User Confirmation Dialog */}
      <AlertDialog open={!!userToRemove} onOpenChange={() => setUserToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove user?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove <strong>{userToRemove?.name}</strong> from the
              platform? This action cannot be undone and they will lose access immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
